import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Controller, useWatch } from 'react-hook-form';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import FloatingSelect from '#/modules/commonComponents/form/select/FloatingSelect';
import InspectionCriteriaFields from '#/modules/imsMgmt/settings/inspectionKind/components/InspectionCriteriaFields';
import {
  INSPECTION_DATA_TYPE_OPTIONS,
  defaultInspectionOperator,
} from '#/modules/imsMgmt/settings/inspectionKind/helper/constants';

const InspectionConditionItemCard = ({
  index,
  control,
  register,
  errors,
  setValue,
  getValues,
  knownDataTypes,
  readOnly = false,
  canRemove,
  onRemove,
}) => {
  const locked = useWatch({ control, name: `items.${index}.locked` });
  const itemCode = useWatch({ control, name: `items.${index}.itemCode` });
  const dataType = useWatch({ control, name: `items.${index}.dataType` });
  const normalizedCode = String(itemCode ?? '').trim().toUpperCase();
  const knownType = knownDataTypes?.[normalizedCode];
  const dataTypeLocked = Boolean(locked || knownType);

  useEffect(() => {
    if (!knownType || dataType === knownType) {
      return;
    }
    setValue(`items.${index}.dataType`, knownType, { shouldDirty: true });
    setValue(
      `items.${index}.operator`,
      defaultInspectionOperator(knownType),
      { shouldDirty: true },
    );
  }, [dataType, index, knownType, setValue]);

  function handleDataTypeChange(nextType) {
    setValue(`items.${index}.operator`, defaultInspectionOperator(nextType), { shouldDirty: true });
    if (nextType !== 'DECIMAL') {
      setValue(`items.${index}.unit`, '', { shouldDirty: true });
    }
    if (nextType === 'BOOLEAN') {
      setValue(`items.${index}.expectedValue`, 'true', { shouldDirty: true });
    }
    if (nextType === 'DECIMAL' && !getValues(`items.${index}.scale`)) {
      setValue(`items.${index}.scale`, '2', { shouldDirty: true });
    }
  }

  function handleItemCodeBlur() {
    const next = String(getValues(`items.${index}.itemCode`) ?? '').trim().toUpperCase();
    setValue(`items.${index}.itemCode`, next, { shouldDirty: true, shouldValidate: true });
  }

  return (
    <div className="mt-3 p-3 border border-border rounded-md">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="mb-0 text-sm font-medium">{`條件 ${index + 1}`}</h3>
        {canRemove ? (
          <Button type="button" size="sm" variant="outline" onClick={onRemove}>
            移除
          </Button>
        ) : null}
      </div>
      <div className="gap-3 grid grid-cols-1 md:grid-cols-2">
        <PlainRegisteredInput
          id={`items.${index}.itemCode`}
          register={register}
          errors={errors}
          readOnly={readOnly || Boolean(locked)}
          mobileLabel="條件代碼"
          ariaLabel="條件代碼"
          registerOptions={{
            onBlur: handleItemCodeBlur,
          }}
        />
        <PlainRegisteredInput
          id={`items.${index}.itemName`}
          register={register}
          errors={errors}
          mobileLabel="條件名稱"
          ariaLabel="條件名稱"
          readOnly={readOnly}
        />
        <FloatingSelect
          id={`items.${index}.dataType`}
          label="資料型別"
          options={INSPECTION_DATA_TYPE_OPTIONS}
          control={control}
          errors={errors}
          isDisabled={readOnly || dataTypeLocked}
          onChange={handleDataTypeChange}
        />
        <Controller
          name={`items.${index}.required`}
          control={control}
          render={({ field }) => (
            <label className={cn('gap-2 flex items-center text-sm')}>
              <Checkbox
                checked={Boolean(field.value)}
                disabled={readOnly}
                onCheckedChange={(checked) => field.onChange(checked === true)}
              />
              必填
            </label>
          )}
        />
        <PlainRegisteredInput
          id={`items.${index}.remark`}
          register={register}
          errors={errors}
          readOnly={readOnly}
          mobileLabel="備註"
          ariaLabel="備註"
        />
      </div>
      <InspectionCriteriaFields
        index={index}
        control={control}
        register={register}
        errors={errors}
        readOnly={readOnly}
      />
    </div>
  );
};

InspectionConditionItemCard.propTypes = {
  index: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  knownDataTypes: PropTypes.object,
  readOnly: PropTypes.bool,
  canRemove: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
};

export default InspectionConditionItemCard;
